<script>
// How a plate's colour dot needs to be edged, shared with Kit's plate tiles so
// a plate looks the same on the pad, on a tile and on the bar.
//
// SAME RULE AS BarSleeve's `tone()`. Pale is light AND unsaturated — white and
// silver, not yellow (yellow is bright but it is a colour, and needs no edge).
// A pale dot gets a hairline on the light ground (`--pale-edge`); a black dot
// gets a rim on the dark ground (`--dark-rim`), or it vanishes into the key.
// Each variable is transparent in the other scheme, so both classes can be
// set without asking which scheme is showing.
export function plateTone(hex) {
  const m = String(hex || '').replace('#', '').match(/../g)
  if (!m || m.length < 3) return { pale: true, dark: false }
  const rgb = m.slice(0, 3).map((h) => parseInt(h, 16) / 255)
  const lin = rgb.map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4))
  const lum = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
  const chroma = Math.max(...rgb) - Math.min(...rgb)
  return { pale: lum > 0.3 && chroma < 0.2, dark: lum < 0.05 }
}
</script>

<script setup>
// The plates you can reach for, one key each. Used by Bar's COUNT mode.
//
// COLOUR NEVER STANDS ALONE. Every key carries its weight as text, because
// a lifter whose gym has black 25s recoloured them in Kit and a colour-blind
// lifter never had the cue in the first place. The dot is a bonus, not the
// label.
//
// NOT CAPPED BY `pairs`. Kit says how many pairs you OWN; a bar you are
// counting was loaded from whatever was on the rack, possibly not yours. A pad
// that refuses the sixth 25 is a pad that cannot count the bar in front of you.
import { computed } from 'vue'
import { fmt } from '../lib/loading.js'

const props = defineProps({
  plates: { type: Array, required: true }, // kit plate records: { weight, colour }
  unit: { type: String, default: 'kg' },
})
defineEmits(['add'])

const keys = computed(() =>
  props.plates.map((p, i) => ({
    key: `${p.id ?? i}-${p.weight}`,
    weight: p.weight,
    label: fmt(Number(p.weight) || 0),
    colour: p.colour || null,
    ...plateTone(p.colour),
  })),
)
</script>

<template>
  <div class="pad" role="group" aria-label="Plates">
    <button
      v-for="p in keys"
      :key="p.key"
      type="button"
      class="plate-key"
      :aria-label="`Add ${p.label} ${unit} to each side`"
      @click="$emit('add', p.weight)"
    >
      <span
        class="dot"
        :class="{ pale: p.pale, rim: p.dark }"
        :style="{ background: p.colour || 'var(--faint)' }"
        aria-hidden="true"
      />
      <span class="weight">{{ p.label }}</span>
    </button>
  </div>
</template>

<style scoped>
/* Three across, as the design draws it: a 3×3 kit fills three rows, and each
   key is wide enough for "1.25" at a size read from arm's length. */
.pad {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

/* 52px, the app's primary-target height: this is the control tapped most
   often and the hand reaching for it is chalked. */
.plate-key {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 52px;
  min-width: 0;
  margin: 0;
  padding: 0 8px;
  border: 1px solid var(--field-line);
  border-radius: 14px;
  background: var(--field);
  color: var(--ink);
  font: 500 0.9375rem/1 var(--face-mono);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.plate-key:active { background: var(--seg); }

.dot {
  flex: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

/* The edge is never a hue: chrome carries no colour in this app. */
.dot.pale { box-shadow: inset 0 0 0 1px var(--pale-edge); }
.dot.rim { box-shadow: inset 0 0 0 1px var(--dark-rim); }
</style>
